/* Phần dùng chung của trang Products: nhãn trạng thái, hàng mở/đóng, thẻ bài mẫu. */
import { useState, type ReactNode } from "react"
import { Badge } from "@/components/ui/badge"
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from "@/components/ui/collapsible"
import { Icon, type AnyIconName } from "@/components/icon"
import { cn } from "@/lib/utils"

export const productStatusLabel: Record<string, string> = {
  draft: "Đang lưu tạm",
  trial_queued: "Đang chấm thử",
  trial_scored: "Đã chấm thử",
  final_queued: "Đang chấm thật",
  final_scored: "Đã chấm thật",
  failed: "Cần thử lại",
}

type BadgeVariant = "default" | "success" | "warning" | "outline" | "destructive" | "secondary"
export const productStatusVariant: Record<string, BadgeVariant> = {
  draft: "outline",
  trial_queued: "warning",
  trial_scored: "default",
  final_queued: "warning",
  final_scored: "success",
  failed: "destructive",
}

export const QUEUED = ["trial_queued", "final_queued"]

export function StatusBadge({ status }: { status?: string }) {
  if (!status) return <Badge variant="outline" className="h-auto px-2.5 py-1 text-sm">Chưa gửi bài</Badge>
  return (
    <Badge variant={productStatusVariant[status] || "secondary"} className="h-auto px-2.5 py-1 text-sm">
      {QUEUED.includes(status) && <span className="size-2 animate-pulse rounded-full bg-current" />}
      {productStatusLabel[status] || status}
    </Badge>
  )
}

/** Một hàng điều hướng: mặc định đóng, bấm mới mở. */
export function NavRow({ icon, title, hint, children }: { icon: AnyIconName; title: string; hint?: string; children: ReactNode }) {
  const [open, setOpen] = useState(false)
  return (
    <Collapsible open={open} onOpenChange={setOpen} className="rounded-[20px] border-[1.5px] border-edge bg-card shadow-card">
      <CollapsibleTrigger className="flex w-full items-center gap-3 rounded-[20px] px-4 py-3.5 text-left transition-colors hover:bg-soft/60 sm:px-5">
        <span className="grid size-10 shrink-0 place-items-center rounded-xl bg-soft">
          <Icon name={icon} className="size-5" />
        </span>
        <span className="min-w-0 flex-1 font-heading text-base font-semibold text-ink">{title}</span>
        {hint && <span className="hidden shrink-0 text-sm text-muted-foreground sm:inline">{hint}</span>}
        <Icon name="chevron-down" className={cn("size-4 shrink-0 text-muted-foreground transition-transform", open && "rotate-180")} />
      </CollapsibleTrigger>
      <CollapsibleContent>
        <div className="flex flex-col gap-3 border-t border-line px-4 py-4 text-base sm:px-5">{children}</div>
      </CollapsibleContent>
    </Collapsible>
  )
}

/** Ảnh nằm ở R2 riêng tư, đi qua BFF có xác thực. `v` đổi khi thay ảnh để bỏ cache cũ. */
export function submissionImageSrc(slug: string, img: any) {
  if (!img?.id) return ""
  return `/api/classes/${encodeURIComponent(slug)}/submission-images/${encodeURIComponent(img.id)}${img.version ? `?v=${encodeURIComponent(img.version)}` : ""}`
}

/** Một bài mẫu — mặc định đóng: chỉ thấy tên và điểm. */
export function ExemplarCard({ exemplar, slug }: { exemplar: any; slug: string }) {
  const [open, setOpen] = useState(false)
  const src = submissionImageSrc(slug, exemplar.evidence_image)
  const facts: [string, string][] = [
    ["Dành cho ai", exemplar.audience],
    ["Nhu cầu", exemplar.audience_need],
    ["Giải pháp", exemplar.solution],
  ]
  return (
    <Collapsible open={open} onOpenChange={setOpen} className="rounded-2xl bg-sand">
      <CollapsibleTrigger className="flex w-full items-center gap-3 rounded-2xl px-4 py-3 text-left">
        <span className="flex min-w-0 flex-1 flex-wrap items-center gap-2">
          <strong className="text-ink">{exemplar.author}</strong>
          {exemplar.is_mine && <Badge variant="solid">Bài của con</Badge>}
        </span>
        {exemplar.score != null && <span className="shrink-0 font-heading font-bold text-orange-strong">{exemplar.score} điểm</span>}
        <Icon name="chevron-down" className={cn("size-4 shrink-0 text-muted-foreground transition-transform", open && "rotate-180")} />
      </CollapsibleTrigger>
      <CollapsibleContent>
        <div className="flex flex-col gap-3 px-4 pb-4">
          {src && <img src={src} alt="Ảnh bằng chứng của bài mẫu" loading="lazy" className="max-h-80 w-full rounded-xl border border-line bg-card object-contain" />}
          {exemplar.artifact_url && (
            <a href={exemplar.artifact_url} target="_blank" rel="noreferrer" className="inline-flex items-center gap-1.5 font-semibold text-orange-strong hover:underline">
              Xem bài mẫu <Icon name="arrow-right" className="size-4" />
            </a>
          )}
          {exemplar.note && <p className="italic text-muted-foreground">“{exemplar.note}”</p>}
          <dl className="grid gap-3 sm:grid-cols-3">
            {facts.map(([label, value]) => (
              <div key={label} className="flex flex-col gap-1 rounded-xl bg-card p-3">
                <dt className="text-sm font-bold tracking-wide text-muted-foreground uppercase">{label}</dt>
                <dd className="text-sm text-ink">{value || "—"}</dd>
              </div>
            ))}
          </dl>
        </div>
      </CollapsibleContent>
    </Collapsible>
  )
}

/** Kết quả chấm thử theo rubric: mỗi chỉ báo một hàng, mức đạt tô trên thang 1–5. */
export function TrialRubric({ rubric, result }: { rubric: any; result: any }) {
  const resultCriteria = Array.isArray(result?.criteria) ? result.criteria : []
  if (!rubric?.criteria?.length || !resultCriteria.length) return null
  const gotBy = new Map<string, any>()
  resultCriteria.forEach((c: any) => (c.indicators || []).forEach((i: any) => gotBy.set(i.indicator_id, i)))
  return (
    <div className="flex flex-col gap-4">
      <p className="text-sm font-bold tracking-[0.08em] text-muted-foreground uppercase">Rubric & mức đạt (sơ bộ)</p>
      {rubric.criteria.map((criterion: any) => (
        <div key={criterion.id} className="flex flex-col gap-2">
          <h4 className="font-heading font-semibold text-ink">
            {criterion.position}. {criterion.name}
          </h4>
          {criterion.indicators.map((indicator: any) => {
            const got = gotBy.get(indicator.id)
            const levels: any[] = indicator.levels || []
            const hit = levels.find((lv) => lv.order === got?.score)
            return (
              <div key={indicator.id} className="flex flex-col gap-2 rounded-xl bg-card p-3">
                <div className="flex flex-wrap items-center justify-between gap-2">
                  <span className="font-semibold text-ink">{indicator.label}</span>
                  {hit && <Badge>{hit.label}</Badge>}
                </div>
                <div className="flex gap-1" aria-label={hit ? `Mức ${hit.order}/${levels.length}: ${hit.label}` : "Chưa có mức"}>
                  {levels.map((lv) => (
                    <span
                      key={lv.code}
                      title={`${lv.label}: ${lv.description}`}
                      className={cn("h-2 flex-1 rounded-full", got?.score != null && lv.order <= got.score ? "bg-orange" : "bg-soft")}
                    />
                  ))}
                </div>
                {got?.feedback && <p className="text-sm text-muted-foreground">{got.feedback}</p>}
              </div>
            )
          })}
        </div>
      ))}
    </div>
  )
}
